import React, { useEffect, useState } from 'react'
import { CButton } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../../shared/icons'
import { useBostonState } from '../lib/useBoston'
import { useUI } from '../../../shared/ui/UIProvider'
import { AI_PRICING, aiUsageLoad, aiUsageSave } from '../lib/ai'
import { biaListScriptCosts, biaScriptCostsSummary } from '../lib/biaApi'

/* Uso de tokens IA — ahora con historial REAL desde la DB (ai_script_costs):
   el backend registra cada llamada a Anthropic con tokens exactos (incluido
   cache) y costo USD calculado server-side. Se agrupa por script y por corrida.
   El contador localStorage de siempre queda abajo como estimado local/offline. */

const box = { textAlign: 'center', marginBottom: 0 }
const kpiLabel = { fontSize: 11, color: '#768192' }

function fmtCost(c) {
  return '$' + Number(c || 0).toFixed(4)
}
function fmtNum(n) {
  return Number(n || 0).toLocaleString()
}
function fmtDate(d) {
  return String(d || '').slice(0, 16).replace('T', ' ')
}

function AiUsageView() {
  useBostonState()
  const { confirm } = useUI()
  const [, setTick] = useState(0)
  const [days, setDays] = useState(30)
  const [costRows, setCostRows] = useState(null)
  const [summary, setSummary] = useState(null) // null = cargando, [] = vacío
  const [detail, setDetail] = useState(null) // { script, rows }
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    setSummary(null)
    setCostRows(null)
    setError('')
    Promise.all([
      biaScriptCostsSummary(days),
      biaListScriptCosts({ days, limit: 1000 }),
    ])
      .then(([summaryRows, rows]) => {
        if (!alive) return
        setSummary(summaryRows)
        setCostRows(rows)
      })
      .catch((e) => {
        if (alive) {
          setSummary([])
          setCostRows([])
          setError(e && e.message ? e.message : String(e))
        }
      })
    return () => { alive = false }
  }, [days])

  async function openDetail(row) {
    setDetail({ script: row.script, rows: null })
    try {
      const filter = row.scriptUuid ? { scriptUuid: row.scriptUuid, days } : { scriptName: row.script, days }
      const rows = await biaListScriptCosts({ ...filter, limit: 200 })
      setDetail({ script: row.script, rows })
    } catch (e) {
      setDetail({ script: row.script, rows: [], error: e && e.message ? e.message : String(e) })
    }
  }

  const totals = (summary || []).reduce(
    (acc, r) => ({
      usd: acc.usd + Number(r.costUsd || 0),
      calls: acc.calls + Number(r.calls || 0),
      tin: acc.tin + Number(r.inputTokens || 0),
      tout: acc.tout + Number(r.outputTokens || 0),
      cache: acc.cache + Number(r.cacheReadTokens || 0),
    }),
    { usd: 0, calls: 0, tin: 0, tout: 0, cache: 0 },
  )

  // ---- estimado local (legacy) ----
  const u = aiUsageLoad()
  async function resetLocal() {
    const ok = await confirm({
      title: 'Uso de tokens IA',
      body: 'Reset del contador LOCAL (estimado)? El historial en DB no se toca.',
      confirmText: 'Reset',
      cancelText: 'Cancelar',
    })
    if (!ok) return
    aiUsageSave({ history: [], totalIn: 0, totalOut: 0, totalCost: 0, totalCalls: 0 })
    setTick((t) => t + 1)
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Uso de tokens IA</h1>
          <p className="text-muted" style={{ margin: 0 }}>
            Historial real desde la DB — costo exacto por script y por corrida (incluye cache).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[7, 30, 90].map((d) => (
            <CButton
              key={d}
              size="sm"
              color={days === d ? 'primary' : 'secondary'}
              variant={days === d ? undefined : 'ghost'}
              onClick={() => setDays(d)}
            >
              {d} días
            </CButton>
          ))}
        </div>
      </div>

      {/* KPIs reales (DB) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 18 }}>
        <div className="card-x" style={box}>
          <div style={kpiLabel}>USD ({days} días)</div>
          <div className="text-warning" style={{ fontSize: 32, fontWeight: 700 }}>{fmtCost(totals.usd)}</div>
        </div>
        <div className="card-x" style={box}>
          <div style={kpiLabel}>Llamadas</div>
          <div className="text-success" style={{ fontSize: 32, fontWeight: 700 }}>{fmtNum(totals.calls)}</div>
        </div>
        <div className="card-x" style={box}>
          <div style={kpiLabel}>Tokens</div>
          <div className="text-info" style={{ fontSize: 24, fontWeight: 700 }}>{fmtNum(totals.tin + totals.tout)}</div>
          <div style={kpiLabel}>in: {fmtNum(totals.tin)} · out: {fmtNum(totals.tout)}</div>
        </div>
        <div className="card-x" style={box}>
          <div style={kpiLabel}>Cache leído</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0ea5e9' }}>{fmtNum(totals.cache)}</div>
          <div style={kpiLabel}>tokens a ~10% de costo</div>
        </div>
      </div>

      {/* Costo por script (DB) */}
      <div className="card-x">
        <div className="bia-card-head">
          <h3>💰 Costo por script (DB)</h3>
          <span className="bia-pill">{summary ? summary.length : '…'}</span>
        </div>
        {error ? (
          <p className="text-danger" style={{ fontSize: 12 }}>
            No se pudo leer el historial de la DB: {error}. ¿Está desplegado el backend con el recurso “costs” y creada la tabla ai_script_costs?
          </p>
        ) : null}
        <div className="bia-scroll" style={{ maxHeight: 420 }}>
          {summary === null ? (
            <p className="bia-empty">Cargando…</p>
          ) : summary.length === 0 ? (
            <p className="bia-empty">Sin registros en la DB para este período.</p>
          ) : (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Script</th>
                  <th style={{ textAlign: 'right' }}>Llamadas</th>
                  <th style={{ textAlign: 'right' }}>Tokens in/out</th>
                  <th style={{ textAlign: 'right' }}>Cache</th>
                  <th style={{ textAlign: 'right' }}>USD</th>
                  <th>Última</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {summary.map((r, i) => (
                  <tr key={i}>
                    <td><strong style={{ fontSize: 12.5 }}>{r.script}</strong></td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.calls)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <small>{fmtNum(r.inputTokens)} / {fmtNum(r.outputTokens)}</small>
                    </td>
                    <td style={{ textAlign: 'right' }}><small>{fmtNum(r.cacheReadTokens)}</small></td>
                    <td style={{ textAlign: 'right' }}><strong>{fmtCost(r.costUsd)}</strong></td>
                    <td><small>{fmtDate(r.lastDate)}</small></td>
                    <td>
                      <CButton size="sm" color="info" variant="ghost" onClick={() => openDetail(r)}>
                        Detalle
                      </CButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Listado completo por llamada */}
      <div className="card-x">
        <div className="bia-card-head">
          <h3>Listado completo del rango</h3>
          <span className="bia-pill">{costRows ? costRows.length : '...'}</span>
        </div>
        <div className="bia-scroll" style={{ maxHeight: 420 }}>
          {costRows === null ? (
            <p className="bia-empty">Cargando...</p>
          ) : costRows.length === 0 ? (
            <p className="bia-empty">Sin llamadas registradas en este rango.</p>
          ) : (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Cuando</th>
                  <th>Script</th>
                  <th>Agente</th>
                  <th>Modelo</th>
                  <th style={{ textAlign: 'right' }}>Tokens in/out</th>
                  <th style={{ textAlign: 'right' }}>Cache</th>
                  <th style={{ textAlign: 'right' }}>USD</th>
                </tr>
              </thead>
              <tbody>
                {costRows.map((c, i) => (
                  <tr key={c.id || i}>
                    <td><small>{fmtDate(c.createdDate)}</small></td>
                    <td><strong style={{ fontSize: 12.5 }}>{c.scriptName || c.scriptUuid || '(sin contexto)'}</strong></td>
                    <td><span className="bia-pill mini">{c.agent}</span></td>
                    <td><code style={{ fontSize: 10.5 }}>{c.model}</code></td>
                    <td style={{ textAlign: 'right' }}>
                      <small>{fmtNum(c.inputTokens)} / {fmtNum(c.outputTokens)}</small>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <small>{fmtNum(c.cacheReadTokens)} / {fmtNum(c.cacheWriteTokens)}</small>
                    </td>
                    <td style={{ textAlign: 'right' }}><strong>{fmtCost(c.costUsd)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detalle por llamada del script elegido */}
      {detail ? (
        <div className="card-x">
          <div className="bia-card-head">
            <h3>Detalle · {detail.script}</h3>
            <CButton size="sm" color="secondary" variant="ghost" onClick={() => setDetail(null)}>Cerrar</CButton>
          </div>
          {detail.error ? <p className="text-danger" style={{ fontSize: 12 }}>{detail.error}</p> : null}
          <div className="bia-scroll" style={{ maxHeight: 360 }}>
            {detail.rows === null ? (
              <p className="bia-empty">Cargando…</p>
            ) : detail.rows.length === 0 ? (
              <p className="bia-empty">Sin llamadas registradas.</p>
            ) : (
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Cuándo</th>
                    <th>Agente</th>
                    <th>Modelo</th>
                    <th>Corrida</th>
                    <th style={{ textAlign: 'right' }}>in/out</th>
                    <th style={{ textAlign: 'right' }}>cache r/w</th>
                    <th style={{ textAlign: 'right' }}>USD</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.rows.map((c, i) => (
                    <tr key={c.id || i}>
                      <td><small>{fmtDate(c.createdDate)}</small></td>
                      <td><span className="bia-pill mini">{c.agent}</span></td>
                      <td><code style={{ fontSize: 10.5 }}>{c.model}</code></td>
                      <td><small>{String(c.runId || '').slice(-8)}</small></td>
                      <td style={{ textAlign: 'right' }}><small>{fmtNum(c.inputTokens)} / {fmtNum(c.outputTokens)}</small></td>
                      <td style={{ textAlign: 'right' }}><small>{fmtNum(c.cacheReadTokens)} / {fmtNum(c.cacheWriteTokens)}</small></td>
                      <td style={{ textAlign: 'right' }}><strong>{fmtCost(c.costUsd)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {/* Estimado local (legacy, localStorage) */}
      <div className="card-x">
        <div className="bia-card-head">
          <h3>Estimado local (este navegador)</h3>
          <CButton color="danger" variant="ghost" size="sm" onClick={resetLocal}>
            <CIcon icon={icon('cilTrash')} /> Reset local
          </CButton>
        </div>
        <p className="text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
          Contador aproximado en localStorage (no incluye cache ni otras PCs). La fuente de verdad es la tabla de arriba.
        </p>
        <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
          <span>Llamadas: <strong>{fmtNum(u.totalCalls)}</strong></span>
          <span>Tokens: <strong>{fmtNum(u.totalIn + u.totalOut)}</strong></span>
          <span>USD est.: <strong>{fmtCost(u.totalCost)}</strong></span>
        </div>
        <small className="text-muted">
          Precios estimación local (USD/M tokens):{' '}
          {Object.entries(AI_PRICING).map(([m, p]) => `${m} $${p.in}/$${p.out}`).join(' · ')}
        </small>
      </div>
    </>
  )
}

export default AiUsageView
